import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Monitor, 
  Settings, 
  FileText, 
  Server, 
  Shield, 
  Activity,
  Download,
  FolderOpen,
  File,
  Edit3,
  Save,
  Trash2,
  Plus,
  Search,
  Upload,
  ChevronRight,
  ChevronDown,
  Home,
  Database,
  Cpu,
  HardDrive,
  Wifi,
  Users,
  Globe
} from 'lucide-react';

const VPSDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('file_manager');
  const [currentPath, setCurrentPath] = useState('/var/www/clara360');
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewFileDialog, setShowNewFileDialog] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const fileInputRef = useRef(null);

  // System Flow Cockpit Data
  const [systemData, setSystemData] = useState({
    clara_ki: {
      status: 'online',
      port: 5003,
      memory: '67MB',
      uptime: '8h',
      restarts: 31
    },
    supabase: {
      status: 'connected',
      tables: 15,
      rls: 'active'
    },
    system: {
      load: 0.1,
      memory: '19%',
      disk: '4.5%'
    },
    ai: {
      ollama: 'ready',
      port: 11434,
      models: 2
    }
  });

  // Routing Map Data - Live Service Overview
  const [routingMap, setRoutingMap] = useState(null);
  const [routingMapLoading, setRoutingMapLoading] = useState(false);

  // Load routing map data
  const loadRoutingMap = async () => {
    setRoutingMapLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add authorization using Supabase token from localStorage
      const supabaseAuth = localStorage.getItem('sb-anhomormslputicoybng-auth-token');
      if (supabaseAuth) {
        try {
          const authData = JSON.parse(supabaseAuth);
          if (authData.access_token) {
            headers['Authorization'] = `Bearer ${authData.access_token}`;
          }
        } catch (e) {
          console.warn('Failed to parse auth token:', e);
        }
      }

      const response = await fetch('/api/vps/routing-map', {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setRoutingMap(data);
    } catch (error) {
      console.error('Failed to load routing map:', error);
      setRoutingMap({ error: error.message });
    } finally {
      setRoutingMapLoading(false);
    }
  };

  // Load directory contents using direct filesystem access
  const loadDirectory = async (path) => {
    setIsLoading(true);
    try {
      // Use Clara JWT Server API for file operations
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add authorization using Supabase token from localStorage
      const supabaseAuth = localStorage.getItem('sb-anhomormslputicoybng-auth-token');
      if (supabaseAuth) {
        try {
          const authData = JSON.parse(supabaseAuth);
          if (authData.access_token) {
            headers['Authorization'] = `Bearer ${authData.access_token}`;
          }
        } catch (e) {
          console.error('Error parsing auth token:', e);
        }
      }

      const response = await fetch(`/api/vps/browse`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ path })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update state with the response data
      setFiles(data.items || []);
      setCurrentPath(data.currentPath || path);
    } catch (error) {
      console.error('Error loading directory:', error);
      // Fallback: Create mock file list for demonstration
      const mockFiles = [
        {
          name: '.env',
          type: 'file',
          size: 1024,
          modified: new Date().toISOString(),
          isHidden: true
        },
        {
          name: 'clara_jwt_server.cjs',
          type: 'file',
          size: 45678,
          modified: new Date().toISOString(),
          isHidden: false
        },
        {
          name: 'src',
          type: 'directory',
          size: 0,
          modified: new Date().toISOString(),
          isHidden: false
        },
        {
          name: 'package.json',
          type: 'file',
          size: 2048,
          modified: new Date().toISOString(),
          isHidden: false
        },
        {
          name: 'vps_file_explorer_api.cjs',
          type: 'file',
          size: 12345,
          modified: new Date().toISOString(),
          isHidden: false
        }
      ];
      setFiles(mockFiles);
      setCurrentPath(path);
    }
    setIsLoading(false);
  };

  // Load file content
  const loadFileContent = async (filePath) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add authorization using Supabase token from localStorage
      const supabaseAuth = localStorage.getItem('sb-anhomormslputicoybng-auth-token');
      if (supabaseAuth) {
        try {
          const authData = JSON.parse(supabaseAuth);
          if (authData.access_token) {
            headers['Authorization'] = `Bearer ${authData.access_token}`;
          }
        } catch (e) {
          console.error('Error parsing auth token:', e);
        }
      }

      const response = await fetch(`/api/vps/file-content`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ path: filePath })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setFileContent(data.content || '');
    } catch (error) {
      console.error('Error loading file content:', error);
      setFileContent(`// Error loading file: ${error.message}\n// File: ${filePath}`);
    }
  };

  // Save file content
  const saveFileContent = async () => {
    if (!selectedFile) return;
    
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add authorization using Supabase token from localStorage
      const supabaseAuth = localStorage.getItem('sb-anhomormslputicoybng-auth-token');
      if (supabaseAuth) {
        try {
          const authData = JSON.parse(supabaseAuth);
          if (authData.access_token) {
            headers['Authorization'] = `Bearer ${authData.access_token}`;
          }
        } catch (e) {
          console.error('Error parsing auth token:', e);
        }
      }

      const response = await fetch(`/api/vps/save-file`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          path: `${currentPath}/${selectedFile.name}`,
          content: fileContent 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      setIsEditing(false);
      alert('File saved successfully!');
    } catch (error) {
      console.error('Error saving file:', error);
      alert(`Error saving file: ${error.message}`);
    }
  };

  // Handle file click
  const handleFileClick = (file) => {
    if (file.type === 'directory') {
      const newPath = currentPath === '/' ? `/${file.name}` : `${currentPath}/${file.name}`;
      loadDirectory(newPath);
    } else {
      setSelectedFile(file);
      loadFileContent(`${currentPath}/${file.name}`);
      setIsEditing(false);
    }
  };

  // Handle parent directory navigation
  const handleParentDirectory = () => {
    const basePath = '/var/www/clara360';
    
    // If we're already at the base path, don't go higher
    if (currentPath === basePath) {
      console.log('Already at base path, cannot go higher');
      return;
    }
    
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
    
    // If the parent would be above base path, go to base path instead
    if (parentPath.length < basePath.length || !parentPath.startsWith(basePath)) {
      console.log('Parent path would be above base path, navigating to base path instead');
      loadDirectory(basePath);
      return;
    }
    
    // Navigate to parent directory
    loadDirectory(parentPath);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Unknown';
    }
  };

  // Get file icon
  const getFileIcon = (file) => {
    if (file.type === 'directory') {
      return <FolderOpen className="w-4 h-4 text-blue-400" />;
    }
    
    const ext = file.name.split('.').pop().toLowerCase();
    switch (ext) {
      case 'js':
      case 'jsx':
      case 'cjs':
        return <File className="w-4 h-4 text-yellow-400" />;
      case 'json':
        return <File className="w-4 h-4 text-green-400" />;
      case 'env':
        return <File className="w-4 h-4 text-red-400" />;
      default:
        return <File className="w-4 h-4 text-gray-400" />;
    }
  };

  // Filter files based on search
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load initial directory and routing map
  useEffect(() => {
    loadDirectory(currentPath);
    loadRoutingMap();
  }, []);

  // System Flow Cockpit Component
  const SystemFlowCockpit = () => (
    <div className="mb-6 p-4 bg-gradient-to-r from-yellow-900/20 to-yellow-800/20 border border-yellow-600/30 rounded-lg">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
        <h3 className="text-lg font-semibold text-yellow-400">Clara360 System Flow - Live Status</h3>
      </div>
      
      {/* Status Cards */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-gray-800/50 border border-green-600/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Clara KI</span>
          </div>
          <div className="text-xs text-gray-300">
            <div>Port: {systemData.clara_ki.port}</div>
            <div>Memory: {systemData.clara_ki.memory}</div>
            <div>Uptime: {systemData.clara_ki.uptime}</div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 border border-blue-600/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">Supabase</span>
          </div>
          <div className="text-xs text-gray-300">
            <div>Status: {systemData.supabase.status}</div>
            <div>Tables: {systemData.supabase.tables}</div>
            <div>RLS: {systemData.supabase.rls}</div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 border border-purple-600/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Cpu className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-purple-400">System</span>
          </div>
          <div className="text-xs text-gray-300">
            <div>Load: {systemData.system.load}</div>
            <div>Memory: {systemData.system.memory}</div>
            <div>Disk: {systemData.system.disk}</div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 border border-cyan-600/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">KI/AI</span>
          </div>
          <div className="text-xs text-gray-300">
            <div>Ollama: {systemData.ai.ollama}</div>
            <div>Port: {systemData.ai.port}</div>
            <div>Models: {systemData.ai.models}</div>
          </div>
        </div>
      </div>
      
      {/* Live-Dateien Widget */}
      <div className="text-xs text-gray-400">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-yellow-400">Live Service Mapping</h4>
          <button 
            onClick={loadRoutingMap}
            disabled={routingMapLoading}
            className="px-2 py-1 bg-yellow-600/20 hover:bg-yellow-600/30 rounded text-xs border border-yellow-600/30"
          >
            {routingMapLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        
        {routingMap?.error ? (
          <div className="text-red-400 text-xs">Error: {routingMap.error}</div>
        ) : routingMap ? (
          <div className="grid grid-cols-3 gap-4">
            {/* PM2 Apps */}
            <div>
              <div className="font-medium text-green-400 mb-1">
                PM2 Apps ({routingMap.summary?.runningPM2Apps}/{routingMap.summary?.totalPM2Apps}):
              </div>
              <div className="space-y-1 max-h-20 overflow-y-auto">
                {routingMap.pm2Apps?.slice(0, 4).map((app, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${app.status === 'online' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                    <span className="truncate">{app.name}</span>
                    {app.restarts > 0 && <span className="text-orange-400">({app.restarts}↻)</span>}
                  </div>
                ))}
                {routingMap.pm2Apps?.length > 4 && (
                  <div className="text-gray-500">... and {routingMap.pm2Apps.length - 4} more</div>
                )}
              </div>
            </div>
            
            {/* Active Ports */}
            <div>
              <div className="font-medium text-blue-400 mb-1">
                Active Ports ({routingMap.summary?.totalPorts}):
              </div>
              <div className="space-y-1 max-h-20 overflow-y-auto">
                {routingMap.listeners?.slice(0, 4).map((listener, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                    <span>:{listener.port}</span>
                    <span className="text-gray-500 truncate">→ {listener.process?.cmd || 'unknown'}</span>
                  </div>
                ))}
                {routingMap.listeners?.length > 4 && (
                  <div className="text-gray-500">... and {routingMap.listeners.length - 4} more</div>
                )}
              </div>
            </div>
            
            {/* Scripts & Files */}
            <div>
              <div className="font-medium text-purple-400 mb-1">
                Scripts ({routingMap.summary?.totalScripts}):
              </div>
              <div className="space-y-1 max-h-20 overflow-y-auto">
                {routingMap.scripts?.slice(0, 4).map((script, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                    <span className="truncate" title={script.path}>
                      {script.path?.split('/').pop() || 'unknown'}
                    </span>
                    <span className="text-gray-500">({script.sha1})</span>
                  </div>
                ))}
                {routingMap.scripts?.length > 4 && (
                  <div className="text-gray-500">... and {routingMap.scripts.length - 4} more</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-xs">Loading routing map...</div>
        )}
        
        {/* Copy for Handover Button */}
        {routingMap && !routingMap.error && (
          <div className="mt-3 pt-2 border-t border-gray-700">
            <button 
              onClick={() => {
                const handoverText = routingMap.pm2Apps?.map(app => 
                  `${app.name} → ${app.script} (${app.status}, restarts: ${app.restarts})`
                ).join('\n') || 'No PM2 apps found';
                navigator.clipboard.writeText(`Clara360 Service Overview:\n${handoverText}\n\nGenerated: ${new Date().toLocaleString()}`);
                alert('Service overview copied to clipboard!');
              }}
              className="px-3 py-1 bg-yellow-600/20 hover:bg-yellow-600/30 rounded text-xs border border-yellow-600/30"
            >
              📋 Copy for Handover
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'system_overview':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <Monitor className="w-6 h-6 text-blue-400" />
                <h3 className="text-lg font-semibold">System Resources</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">CPU Usage</span>
                  <span className="text-green-400">12%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Memory</span>
                  <span className="text-green-400">19% (8.2GB / 43.2GB)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Disk Space</span>
                  <span className="text-green-400">4.5% (19GB / 465GB)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Load Average</span>
                  <span className="text-green-400">0.1</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <Wifi className="w-6 h-6 text-green-400" />
                <h3 className="text-lg font-semibold">Network</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Interface</span>
                  <span className="text-green-400">ens6</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">IP Address</span>
                  <span className="text-green-400">217.154.242.134</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="text-green-400">Connected</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <HardDrive className="w-6 h-6 text-purple-400" />
                <h3 className="text-lg font-semibold">Storage</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total</span>
                  <span className="text-green-400">465GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Used</span>
                  <span className="text-yellow-400">19GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Available</span>
                  <span className="text-green-400">447GB</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'service_control':
        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-600/30 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                <h2 className="text-2xl font-bold text-blue-400">Service System DNA</h2>
              </div>
              <p className="text-gray-300">Vollständige Übersicht aller Services, Ports und kritischen Dateien - Live vom Server</p>
              
              {routingMap && !routingMap.error && (
                <div className="mt-4 flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span>{routingMap.summary?.runningPM2Apps || 0} Services aktiv</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                    <span>{routingMap.summary?.totalPorts || 0} Ports offen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
                    <span>{routingMap.summary?.totalScripts || 0} Script-Dateien</span>
                  </div>
                  <button 
                    onClick={loadRoutingMap}
                    disabled={routingMapLoading}
                    className="ml-auto px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 rounded border border-blue-600/30 text-xs"
                  >
                    {routingMapLoading ? 'Aktualisiere...' : '🔄 Refresh'}
                  </button>
                </div>
              )}
            </div>

            {routingMap?.error ? (
              <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-6">
                <h3 className="text-red-400 font-semibold mb-2">Fehler beim Laden der Service-Daten</h3>
                <p className="text-gray-300">{routingMap.error}</p>
                <button 
                  onClick={loadRoutingMap}
                  className="mt-3 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 rounded border border-red-600/30"
                >
                  Erneut versuchen
                </button>
              </div>
            ) : !routingMap ? (
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-400">Lade Service-Daten...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                
                {/* Säule 1: NGINX Eingangstore */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Server className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-semibold text-green-400">NGINX Eingangstore</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    Hier beginnt jede Anfrage. NGINX leitet Domains zu internen Services weiter.
                  </p>
                  
                  <div className="space-y-3">
                    {routingMap.nginxSites?.length > 0 ? routingMap.nginxSites.map((site, idx) => (
                      <div key={idx} className="bg-gray-700/50 rounded p-3 border border-green-600/20">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span className="font-medium text-green-300">{site.server_name}</span>
                        </div>
                        {site.api_upstream && (
                          <div className="text-xs text-gray-400">
                            <span className="text-green-400">/api/*</span> → {site.api_upstream}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          📄 {site.file?.split('/').pop()}
                        </div>
                      </div>
                    )) : (
                      <div className="text-gray-500 text-sm italic">Keine NGINX-Konfiguration gefunden</div>
                    )}
                  </div>
                </div>

                {/* Säule 2: Aktive Services (PM2) */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Activity className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-blue-400">Aktive Services</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    PM2-verwaltete Services. Diese Programme verarbeiten deine Anfragen.
                  </p>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {routingMap.pm2Apps?.map((app, idx) => {
                      const isOnline = app.status === 'online';
                      const hasIssues = app.restarts > 10;
                      
                      return (
                        <div key={idx} className={`bg-gray-700/50 rounded p-3 border ${
                          isOnline ? 'border-green-600/20' : 'border-red-600/20'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${
                                isOnline ? 'bg-green-400' : 'bg-red-400'
                              }`}></div>
                              <span className="font-medium">{app.name}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              isOnline ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'
                            }`}>
                              {isOnline ? 'ONLINE' : 'OFFLINE'}
                            </span>
                          </div>
                          
                          {/* Service-Erklärung */}
                          <div className="text-sm text-gray-300 mb-2">
                            {app.name === 'clara-jwt-5003' && '🤖 Clara KI Hauptserver - Verarbeitet alle Gespräche'}
                            {app.name === 'vpi-master-fixed' && '📁 VPS Datei-Manager - Ermöglicht Dateizugriff'}
                            {app.name === 'clara-gemini-probe-5104' && '🧠 KI-Engine - Verbindung zu Google Gemini'}
                            {app.name === 'clara-clean' && '🧹 Wartungs-Service - Automatische Systemreinigung'}
                            {!['clara-jwt-5003', 'vpi-master-fixed', 'clara-gemini-probe-5104', 'clara-clean'].includes(app.name) && 
                              '⚙️ System-Service - Unterstützende Funktionen'}
                          </div>
                          
                          <div className="text-xs text-gray-400 space-y-1">
                            <div>📄 {app.script?.split('/').pop() || 'Unbekannte Datei'}</div>
                            {app.portGuess && <div>🔌 Port: {app.portGuess}</div>}
                            <div className="flex items-center gap-3">
                              <span>💾 {Math.round(app.memory / 1024 / 1024)}MB</span>
                              <span>⚡ {app.cpu}% CPU</span>
                              {hasIssues && (
                                <span className="text-orange-400">⚠️ {app.restarts} Restarts</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Säule 3: Kritische Dateien & Ports */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-semibold text-purple-400">System-Dateien & Ports</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    Wichtige Konfigurationsdateien und alle offenen Netzwerk-Ports.
                  </p>
                  
                  {/* Script-Dateien */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-purple-300 mb-2">📄 Script-Dateien</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {routingMap.scripts?.map((script, idx) => (
                        <div key={idx} className="bg-gray-700/30 rounded p-2 text-xs">
                          <div className="font-medium text-purple-300">
                            {script.path?.split('/').pop()}
                          </div>
                          <div className="text-gray-400">
                            SHA1: {script.sha1} • {Math.round(script.size / 1024)}KB
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Aktive Ports */}
                  <div>
                    <h4 className="text-sm font-medium text-purple-300 mb-2">🔌 Aktive Ports</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {routingMap.listeners?.slice(0, 8).map((listener, idx) => (
                        <div key={idx} className="bg-gray-700/30 rounded p-2 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-purple-300">:{listener.port}</span>
                            <span className="text-gray-400">{listener.process?.cmd}</span>
                          </div>
                          {listener.process?.script && (
                            <div className="text-gray-500 mt-1">
                              → {listener.process.script.split('/').pop()}
                            </div>
                          )}
                        </div>
                      ))}
                      {routingMap.listeners?.length > 8 && (
                        <div className="text-gray-500 text-xs text-center">
                          ... und {routingMap.listeners.length - 8} weitere Ports
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Copy for Handover - Erweitert */}
            {routingMap && !routingMap.error && (
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-300">Delegation & Handover</h4>
                    <p className="text-sm text-gray-400">Kopiere eine vollständige System-Übersicht für die Zusammenarbeit</p>
                  </div>
                  <button 
                    onClick={() => {
                      const timestamp = new Date().toLocaleString('de-DE');
                      const runningServices = routingMap.pm2Apps?.filter(app => app.status === 'online') || [];
                      const stoppedServices = routingMap.pm2Apps?.filter(app => app.status !== 'online') || [];
                      
                      const handoverText = `
Clara360 Service System DNA - ${timestamp}

🟢 AKTIVE SERVICES (${runningServices.length}):
${runningServices.map(app => 
  `• ${app.name} → ${app.script?.split('/').pop() || 'unknown'} (${Math.round(app.memory/1024/1024)}MB, ${app.restarts} restarts)`
).join('\n')}

🔴 GESTOPPTE SERVICES (${stoppedServices.length}):
${stoppedServices.map(app => 
  `• ${app.name} → ${app.script?.split('/').pop() || 'unknown'}`
).join('\n')}

🔌 WICHTIGE PORTS:
${routingMap.listeners?.slice(0, 5).map(l => 
  `• :${l.port} → ${l.process?.cmd} (${l.process?.script?.split('/').pop() || 'unknown'})`
).join('\n')}

🌐 NGINX ROUTING:
${routingMap.nginxSites?.map(site => 
  `• ${site.server_name} → ${site.api_upstream || 'static'}`
).join('\n')}

📄 KRITISCHE DATEIEN:
${routingMap.scripts?.slice(0, 5).map(script => 
  `• ${script.path?.split('/').pop()} (SHA1: ${script.sha1})`
).join('\n')}
                      `.trim();
                      
                      navigator.clipboard.writeText(handoverText);
                      alert('Vollständige Service-Übersicht in die Zwischenablage kopiert!');
                    }}
                    className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 rounded border border-blue-600/30 flex items-center gap-2"
                  >
                    📋 System-DNA kopieren
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'file_manager':
        return (
          <div className="space-y-4">
            <SystemFlowCockpit />
            
            {/* File Browser - Full Width */}
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              <div className="p-4 border-b border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">File Browser</h3>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => setShowNewFileDialog(true)}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      New File
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-400 mb-2">
                  Current Path: {currentPath}
                </div>
                
                {/* Navigation */}
                <div className="flex items-center gap-2 mb-4">
                  {currentPath !== '/' && (
                    <button
                      onClick={handleParentDirectory}
                      className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      Parent Directory
                    </button>
                  )}
                </div>
              </div>
              
              {/* File List */}
              <div className="p-4">
                {isLoading ? (
                  <div className="text-center py-8 text-gray-400">Loading...</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {filteredFiles.map((file, index) => (
                      <div
                        key={index}
                        onClick={() => handleFileClick(file)}
                        className="flex items-center justify-between p-3 bg-gray-700 hover:bg-gray-600 rounded cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {getFileIcon(file)}
                          <span className={`font-medium ${file.isHidden ? 'text-gray-500' : 'text-white'}`}>
                            {file.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>{formatFileSize(file.size)}</span>
                          <span>{formatDate(file.modified)}</span>
                          {file.type === 'directory' && (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* File Content Editor - Full Width */}
            {selectedFile && (
              <div className="bg-gray-800 rounded-lg border border-gray-700">
                <div className="p-4 border-b border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Edit3 className="w-5 h-5" />
                      {selectedFile.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={saveFileContent}
                            className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-sm flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={() => setIsEditing(false)}
                            className="px-3 py-2 bg-gray-600 hover:bg-gray-700 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center gap-2"
                        >
                          <Edit3 className="w-4 h-4" />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <textarea
                    value={fileContent}
                    onChange={(e) => setFileContent(e.target.value)}
                    readOnly={!isEditing}
                    className={`w-full h-96 bg-gray-900 border border-gray-600 rounded p-4 font-mono text-sm resize-none focus:outline-none ${
                      isEditing ? 'focus:border-blue-500' : 'cursor-default'
                    }`}
                    placeholder="Select a file to view its content..."
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'nginx_config':
        return (
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-green-400" />
              NGINX Configuration
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-700 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Status</span>
                  <span className="text-green-400">Active</span>
                </div>
              </div>
              <div className="p-4 bg-gray-700 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Configuration File</span>
                  <span className="text-blue-400">/etc/nginx/nginx.conf</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'ssh_management':
        return (
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              SSH Management
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-700 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium">SSH Service</span>
                  <span className="text-green-400">Running</span>
                </div>
              </div>
              <div className="p-4 bg-gray-700 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Active Connections</span>
                  <span className="text-blue-400">1</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'processes':
        return (
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              System Processes
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
                <div>
                  <span className="font-medium">clara-jwt-5003</span>
                  <div className="text-sm text-gray-400">Node.js • PID: 1234</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400 text-sm">67MB</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
                <div>
                  <span className="font-medium">ollama</span>
                  <div className="text-sm text-gray-400">AI Service • PID: 5678</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400 text-sm">2.1GB</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'supabase_db':
        return (
          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-400" />
                Supabase Database
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-gray-700 rounded">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Connection Status</span>
                      <span className="text-green-400">Connected</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-700 rounded">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Tables</span>
                      <span className="text-blue-400">15</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-700 rounded">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">RLS Status</span>
                      <span className="text-green-400">Active</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-300">Active Tables:</h4>
                  <div className="space-y-1 text-sm">
                    <div className="text-blue-400">• users</div>
                    <div className="text-blue-400">• clara_conversations</div>
                    <div className="text-blue-400">• clara_personalities</div>
                    <div className="text-blue-400">• user_sessions</div>
                    <div className="text-blue-400">• api_logs</div>
                    <div className="text-gray-400">• ... and 10 more</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="text-center py-8 text-gray-400">Select a tab to view content</div>;
    }
  };

  const tabs = [
    { id: 'system_overview', label: 'System Overview', icon: Monitor },
    { id: 'service_control', label: 'Service System DNA', icon: Settings },
    { id: 'file_manager', label: 'File Manager', icon: FileText },
    { id: 'nginx_config', label: 'NGINX Config', icon: Server },
    { id: 'ssh_management', label: 'SSH Management', icon: Shield },
    { id: 'processes', label: 'Processes', icon: Activity },
    { id: 'supabase_db', label: 'Supabase DB', icon: Database }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Monitor className="w-8 h-8 text-blue-400" />
            <div>
              <h1 className="text-2xl font-bold">VPS Control Center</h1>
              <p className="text-gray-400">Vollständiges System-Management mit maximaler Transparenz</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => {
                // Toggle auto-refresh (placeholder for future implementation)
                alert('Auto-refresh functionality will be implemented in the next update!');
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium"
            >
              Auto-Refresh ON
            </button>
            <button 
              onClick={() => {
                loadRoutingMap();
                loadDirectory(currentPath);
              }}
              disabled={routingMapLoading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {routingMapLoading ? 'Aktualisiere...' : 'Jetzt aktualisieren'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 border-b border-gray-700 pb-4">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {renderTabContent()}
        </div>

        {/* New File Dialog */}
        {showNewFileDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 w-96">
              <h3 className="text-lg font-semibold mb-4">Create New File</h3>
              <input
                type="text"
                placeholder="Enter file name..."
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500 mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowNewFileDialog(false);
                    setNewFileName('');
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Create new file logic here
                    setShowNewFileDialog(false);
                    setNewFileName('');
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VPSDashboard;

